import { doc, getDoc, getFirestore, getDocs, collection, collectionGroup } from 'firebase/firestore';
import { firebaseApp } from '../firebase';
import { BASE_ASSET_URL } from './ttGlbLoader';

export type AssetLayout = 'legacy' | 'regional';

export interface ProjectRegion {
    id: string;
    label?: string;
    modelPath: string;
    metadataPath?: string;
    camerasPath?: string;
    enabled?: boolean;
}

export interface ProjectTrajectoryPoint {
    x: number;
    y: number;
    z: number;
}

export interface ProjectTrajectory {
    id: string;
    label?: string;
    startStation?: number;
    stationIncreasing?: boolean;
    points: ProjectTrajectoryPoint[];
}

export interface ProjectPointCloud {
    custom: Object;
    transformation?: number[];
    path: string;
    id: string;
    name: string;
    subtitle: string;
    loaded: boolean;
    visible: boolean;
    loading: boolean;
    enabled: boolean;
}

export interface ProjectMeta {
    name?: string;
    subtitle?: string;
    description?: string;
    transformation?: number[];
    modelNames?: string[];
    pointCloudNames?: string[];
    assetLayout?: AssetLayout;
    initialRegion?: string;
    regions?: ProjectRegion[];
    initialTrajectory?: string;
    trajectories?: ProjectTrajectory[];
    projectOwner?: string;
    whiteList?: string[];
    [key: string]: unknown;
}

export interface NormalizedProjectRegion extends ProjectRegion {
    label: string;
    enabled: boolean;
    modelPaths: string[];
    canActivate: boolean;
    validationError?: string;
}

export interface NormalizedProjectConfig {
    projectName: string;
    layout: AssetLayout;
    meta: ProjectMeta;
    regions: NormalizedProjectRegion[];
    initialRegionId: string | null;
    trajectories: ProjectTrajectory[];
    initialTrajectoryId: string | null;
    projectPointClouds: ProjectPointCloud[];
}

function cleanRelativePath(value: string): string {
    return value.trim().replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').replace(/\/{2,}/g, '/');
}

function encodePath(path: string): string {
    return cleanRelativePath(path).split('/').map(encodeURIComponent).join('/');
}

function assetUrl(projectName: string, path: string): string {
    return `${BASE_ASSET_URL}/models/${encodeURIComponent(projectName)}/${encodePath(path)}`;
}

export function legacyModelUrl(projectName: string, modelFile: string): string {
    return assetUrl(projectName, `models/${modelFile}`);
}

export function legacyMetadataUrl(projectName: string): string {
    return assetUrl(projectName, 'cameras.xml');
}

export function legacyImageUrl(projectName: string, label: string): string {
    return assetUrl(projectName, `cameras/${withImageExtension(label)}`);
}

export function regionalModelUrl(projectName: string, region: Pick<ProjectRegion, 'modelPath'>): string {
    return assetUrl(projectName, region.modelPath);
}

export function regionalMetadataUrl(projectName: string, region: Pick<ProjectRegion, 'metadataPath'>): string | null {
    return region.metadataPath ? assetUrl(projectName, region.metadataPath) : null;
}

export function regionalImageUrl(projectName: string, region: Pick<ProjectRegion, 'camerasPath'>, label: string): string | null {
    return region.camerasPath ? assetUrl(projectName, `${region.camerasPath}/${withImageExtension(label)}`) : null;
}

export function regionalImageUrlTif(projectName: string, region: Pick<ProjectRegion, 'camerasPath'>, label: string): string | null {
    return region.camerasPath ? assetUrl(projectName, `${region.camerasPath}/${withImageExtensionTif(label)}`) : null;
}

function withImageExtension(label: string): string {
    const clean = label.trim();
    return /\.[a-z0-9]+$/i.test(clean) ? clean : `${clean}.JPG`;
}

function withImageExtensionTif(label: string): string {
    const clean = label.trim();
    return /\.[a-z0-9]+$/i.test(clean) ? clean : `${clean}.tif`;
}

function legacyModelNames(projectName: string, meta: ProjectMeta): string[] {
    if (Array.isArray(meta.modelNames) && meta.modelNames.length > 0) {
        return meta.modelNames.map(String).filter(Boolean);
    }
    switch (projectName.toLowerCase()) {
        case 'sentvid': return ['vse_400000_15000.glb'];
        case 'sentvid2': return ['15-2.glb', '15-3.glb', '15-4.glb'];
        case 'logatec': return ['model.glb'];
        default: return ['model0.glb'];
    }
}

function normalizeRegionalRegion(value: unknown): NormalizedProjectRegion | null {
    if (!value || typeof value !== 'object') return null;
    const raw = value as Partial<ProjectRegion>;
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    const modelPath = typeof raw.modelPath === 'string' ? cleanRelativePath(raw.modelPath) : '';
    if (!id || !modelPath) return null;
    const metadataPath = typeof raw.metadataPath === 'string' && raw.metadataPath.trim()
        ? cleanRelativePath(raw.metadataPath) : undefined;
    const camerasPath = typeof raw.camerasPath === 'string' && raw.camerasPath.trim()
        ? cleanRelativePath(raw.camerasPath) : undefined;
    const canActivate = Boolean(metadataPath && camerasPath);
    return {
        id,
        label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : id,
        modelPath,
        modelPaths: [modelPath],
        metadataPath,
        camerasPath,
        enabled: raw.enabled !== false,
        canActivate,
        validationError: canActivate ? undefined : 'Model-only region: metadataPath and camerasPath are required for activation.',
    };
}

function normalizeTrajectoryPoint(value: unknown): ProjectTrajectoryPoint | null {
    let x: unknown;
    let y: unknown;
    let z: unknown;
    if (Array.isArray(value)) {
        [x, y, z] = value;
    } else if (value && typeof value === 'object') {
        ({ x, y, z } = value as Partial<ProjectTrajectoryPoint>);
    } else {
        return null;
    }
    const toCoordinate = (coordinate: unknown): number => {
        if (typeof coordinate === 'number') return coordinate;
        if (typeof coordinate === 'string' && coordinate.trim()) return Number(coordinate);
        return NaN;
    };
    const point = { x: toCoordinate(x), y: toCoordinate(y), z: toCoordinate(z) };
    return Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z)
        ? point
        : null;
}

function normalizeTrajectory(value: unknown): ProjectTrajectory | null {
    if (!value || typeof value !== 'object') return null;
    const raw = value as Partial<ProjectTrajectory>;
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    if (!id || !Array.isArray(raw.points)) return null;
    const points = raw.points
        .map(normalizeTrajectoryPoint)
        .filter((point): point is ProjectTrajectoryPoint => Boolean(point));
    if (points.length < 2 || points.length !== raw.points.length) return null;
    const first = points[0];
    if (!points.slice(1).some((point) => point.x !== first.x || point.y !== first.y || point.z !== first.z)) return null;
    return {
        id,
        label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : id,
        startStation: Number.isFinite(Number(raw.startStation)) ? Number(raw.startStation) : 0,
        stationIncreasing: raw.stationIncreasing !== false,
        points,
    };
}

function normalizeTrajectories(meta: ProjectMeta): {
    trajectories: ProjectTrajectory[];
    initialTrajectoryId: string | null;
} {
    const trajectories = Array.isArray(meta.trajectories)
        ? meta.trajectories
            .map(normalizeTrajectory)
            .filter((trajectory): trajectory is ProjectTrajectory => Boolean(trajectory))
            .filter((trajectory, index, all) => all.findIndex((candidate) => candidate.id === trajectory.id) === index)
        : [];
    const requested = typeof meta.initialTrajectory === 'string' ? meta.initialTrajectory.trim() : '';
    return {
        trajectories,
        initialTrajectoryId: trajectories.find((trajectory) => trajectory.id === requested)?.id
            ?? trajectories[0]?.id
            ?? null,
    };
}

export function normalizeProjectConfig(projectName: string, rawMeta: unknown): NormalizedProjectConfig {
    const meta: ProjectMeta = rawMeta && typeof rawMeta === 'object' ? { ...(rawMeta as ProjectMeta) } : {};
    const layout: AssetLayout = meta.assetLayout === 'regional' ? 'regional' : 'legacy';
    const trajectoryConfig = normalizeTrajectories(meta);

    let projectPointClouds: ProjectPointCloud[] = [];
    for (const name of meta.pointCloudNames || []) {
        const id = name.replace(/\.ply$/i, '');
        const pointCloudEntry: ProjectPointCloud = {
            path: `points/${name}`,
            name: id,
            custom: {},
            id: id,
            subtitle: '',
            loaded: false,
            visible: false,
            loading: false,
            enabled: true,
        }
        projectPointClouds.push(pointCloudEntry);
    }

    if (layout === 'legacy') {
        const modelNames = legacyModelNames(projectName, meta);
        const legacy: NormalizedProjectRegion = {
            id: '__legacy__',
            label: meta.name || projectName,
            modelPath: `models/${modelNames[0]}`,
            modelPaths: modelNames.map((name) => `models/${name}`),
            metadataPath: 'cameras.xml',
            camerasPath: 'cameras',
            enabled: true,
            canActivate: true,
        };
        return { projectName, layout, meta, regions: [legacy], initialRegionId: legacy.id, ...trajectoryConfig, projectPointClouds};
    }

    let regions = Array.isArray(meta.regions)
        ? meta.regions.map(normalizeRegionalRegion).filter((region): region is NormalizedProjectRegion => Boolean(region))
        : [];
    if (regions.length === 0 && Array.isArray(meta.modelNames)) {
        regions = meta.modelNames.map(String).filter(Boolean).map((modelName) => {
            const file = cleanRelativePath(modelName).split('/').pop() || modelName;
            const id = file.replace(/\.glb$/i, '');
            return normalizeRegionalRegion({
                id,
                modelPath: `models/${file}`,
                metadataPath: `meta/${id}.xml`,
                camerasPath: `cameras/${id}`,
            })!;
        });
    }
    regions = regions.filter((region, index, all) => all.findIndex((candidate) => candidate.id === region.id) === index);
    const enabled = regions.filter((region) => region.enabled);
    const requested = typeof meta.initialRegion === 'string' ? meta.initialRegion : null;
    const initial = enabled.find((region) => region.id === requested && region.canActivate)
        || enabled.find((region) => region.canActivate)
        || enabled[0];
    return { projectName, layout, meta, regions, initialRegionId: initial?.id ?? null, ...trajectoryConfig, projectPointClouds };
}

export async function fetchProjectConfig(projectName: string): Promise<NormalizedProjectConfig> {
    const snapshot = await getDoc(doc(getFirestore(firebaseApp), 'projects', projectName));
    if (!snapshot.exists()) throw new Error(`Project projects/${projectName} was not found.`);
    return normalizeProjectConfig(projectName, snapshot.data().meta);
}
export function modelUrl(config: NormalizedProjectConfig, region: NormalizedProjectRegion, modelPath = region.modelPath): string {
    return config.layout === 'legacy'
        ? assetUrl(config.projectName, modelPath)
        : regionalModelUrl(config.projectName, { modelPath });
}
export function metadataUrl(config: NormalizedProjectConfig, region: NormalizedProjectRegion): string | null {
    if (config.layout === 'legacy') return legacyMetadataUrl(config.projectName);
    return regionalMetadataUrl(config.projectName, region);
}

export function imageUrl(config: NormalizedProjectConfig, region: NormalizedProjectRegion, label: string): string | null {
    if (config.layout === 'legacy') return legacyImageUrl(config.projectName, label);
    return regionalImageUrl(config.projectName, region, label);
}

export function imageUrlTif(config: NormalizedProjectConfig, region: NormalizedProjectRegion, label: string): string | null {
    return regionalImageUrlTif(config.projectName, region, label);
}
